import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import BottomDrawer from '../Drawer/Drawer';
import Button from '../Button/Button';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import { ModalCloseIcon, FullWidthApply } from './Common';
import { ReactComponent as InfoBlueIcon } from '../../assets/images/priceAlerts/info-blue.svg';

const DrawerTitle = styled.span`
  font-family: ${variables.family.goldSilver};
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  color: ${themeColors.colorTextPrimary};
`;

const Subtitle = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};
  margin-bottom: ${pxToRem(24)};
`;

const StepRow = styled.div`
  margin-bottom: ${pxToRem(20)};
`;

// Slate "Step N" pill on its own line, so the step title sits below it.
const StepBadge = styled.span`
  display: block;
  width: fit-content;
  height: 16px;
  background: ${themeColors.colorTextSecondary};
  color: ${themeColors.colorWhite};
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-size: 12px;
  line-height: 12%;
  padding: ${pxToRem(10)} ${pxToRem(10)} ${pxToRem(14)} ${pxToRem(10)};
  border-radius: ${pxToRem(2)};
  margin-bottom: ${pxToRem(12)};
`;

const StepTitle = styled.p`
  display: block;
  font-family: ${variables.family.goldSilver};
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  color: ${themeColors.colorTextPrimary};
  margin: 0 0 ${pxToRem(8)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const StepBody = styled.p`
  display: block;
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: ${themeColors.colorTextSecondary};
  margin: 0;
`;

const Callout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(10)};
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: ${pxToRem(8)};
  margin-top: ${pxToRem(4)};
`;

const CalloutIcon = styled(InfoBlueIcon)`
  flex-shrink: 0;
  width: 15px;
  height: 15px;
  margin: 1px 0 0 9.5px;
  color: ${themeColors.colorTextSecondary};
`;

const CalloutText = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};
  margin: 0;
`;

// Intro drawer for the Buy-at-Target-Price order-entry step, mirroring
// PriceAlerts/AlertInfoDrawer but with the two-step "how it works" design.
// Auto-opened once by TargetPriceOrderEntry; "Let's Go" just closes it.
const TargetPriceInfoDrawer = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={<DrawerTitle>{t('gtt.targetPriceIntro.title')}</DrawerTitle>}
      closeIcon={<ModalCloseIcon />}
      headerTightBottom
      maxHeight="80%"
      contentPadding="8px 16px 16px"
      footer={
        <FullWidthApply>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            type="button"
            onClick={onClose}
            fontSize="16px"
            data-cy="gtt-intro-lets-go"
          >
            {t('gtt.common.letsGo')}
          </Button>
        </FullWidthApply>
      }
    >
      <Subtitle>{t('gtt.targetPriceIntro.subtitle')}</Subtitle>

      <StepRow>
        <StepBadge>{t('gtt.targetPriceIntro.step1Label')}</StepBadge>
        <StepTitle>{t('gtt.targetPriceIntro.step1Title')}</StepTitle>
        <StepBody>{t('gtt.targetPriceIntro.step1Body')}</StepBody>
      </StepRow>

      <StepRow>
        <StepBadge>{t('gtt.targetPriceIntro.step2Label')}</StepBadge>
        <StepTitle>{t('gtt.targetPriceIntro.step2Title')}</StepTitle>
        <StepBody>{t('gtt.targetPriceIntro.step2Body')}</StepBody>
      </StepRow>

      <Callout>
        <CalloutIcon />
        <CalloutText>{t('gtt.targetPriceIntro.callout')}</CalloutText>
      </Callout>
    </BottomDrawer>
  );
};

TargetPriceInfoDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default TargetPriceInfoDrawer;
